import React, { useEffect, useState } from "react";
import {
  applyConsent,
  getConsent,
  onOpenConsentSettings,
  saveConsent,
} from "../utils/consent";
import { ADS_ENABLED } from "../config/features";

const font = '"DM Sans", sans-serif';

const Toggle = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 46,
      height: 26,
      borderRadius: 13,
      border: "none",
      padding: 3,
      background: checked ? "#7c3aed" : "#cbd5e1",
      opacity: disabled ? 0.6 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      justifyContent: checked ? "flex-end" : "flex-start",
      flexShrink: 0,
      transition: "background 0.2s",
    }}
  >
    <span
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "#fff",
        display: "block",
      }}
    />
  </button>
);

const Row = ({ title, text, checked, disabled, onChange }) => (
  <div
    style={{
      display: "flex",
      gap: "1rem",
      alignItems: "flex-start",
      padding: "0.85rem 0",
      borderTop: "1px solid #e5e7eb",
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1f2937" }}>
        {title}
      </div>
      <div style={{ fontSize: "0.8rem", color: "#6b7280", lineHeight: 1.5 }}>
        {text}
      </div>
    </div>
    <Toggle
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      label={title}
    />
  </div>
);

const btn = (primary) => ({
  flex: "1 1 130px",
  padding: "0.7rem 1rem",
  borderRadius: 10,
  fontFamily: font,
  fontWeight: 700,
  fontSize: "0.875rem",
  cursor: "pointer",
  border: primary ? "none" : "2px solid #7c3aed",
  background: primary ? "#7c3aed" : "#fff",
  color: primary ? "#fff" : "#7c3aed",
});

const ConsentBanner = () => {
  const [decided, setDecided] = useState(() => !!getConsent());
  const [open, setOpen] = useState(() => !getConsent());
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(() => !!getConsent()?.analytics);
  const [advertising, setAdvertising] = useState(
    () => !!getConsent()?.advertising,
  );

  // Apply any stored choice on app start (loads analytics only if allowed).
  useEffect(() => {
    applyConsent(getConsent());
  }, []);

  // Let any page reopen the panel (e.g. a "Cookie settings" link).
  useEffect(
    () =>
      onOpenConsentSettings(() => {
        const c = getConsent();
        setAnalytics(!!c?.analytics);
        setAdvertising(!!c?.advertising);
        setCustomizing(true);
        setOpen(true);
      }),
    [],
  );

  const choose = (a, b) => {
    saveConsent({ analytics: a, advertising: b });
    setAnalytics(a);
    setAdvertising(b);
    setDecided(true);
    setOpen(false);
    setCustomizing(false);
  };

  if (!open) {
    return decided ? (
      <button
        type="button"
        onClick={() => {
          setCustomizing(true);
          setOpen(true);
        }}
        aria-label="Cookie settings"
        title="Cookie settings"
        style={{
          position: "fixed",
          left: 12,
          bottom: 12,
          zIndex: 900,
          width: 38,
          height: 38,
          borderRadius: "50%",
          border: "1px solid #ddd6fe",
          background: "#fff",
          boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
          opacity: 0.85,
        }}
      >
        🍪
      </button>
    ) : null;
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Privacy and cookie choices"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        display: "flex",
        justifyContent: "center",
        padding: "0.75rem",
        fontFamily: font,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 680,
          maxHeight: "85vh",
          overflowY: "auto",
          background: "#fff",
          color: "#1f2937",
          borderRadius: 16,
          padding: "1.1rem 1.25rem",
          boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
          border: "1px solid #ddd6fe",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: "1.05rem", marginBottom: 6 }}>
          Your privacy choices
        </div>
        <p
          style={{
            margin: "0 0 0.85rem",
            fontSize: "0.85rem",
            lineHeight: 1.55,
            color: "#4b5563",
          }}
        >
          NORYQ needs basic storage to work (login, saved lists, your choices).
          With your permission we also use analytics to improve the app
          {ADS_ENABLED ? " and advertising to keep it free" : ""}. Nothing
          else loads until you choose. You can change this any time. See our{" "}
          <a href="/privacy" style={{ color: "#7c3aed" }}>
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="/cookies" style={{ color: "#7c3aed" }}>
            Cookie Policy
          </a>
          .
        </p>

        {customizing && (
          <div style={{ marginBottom: "0.85rem" }}>
            <Row
              title="Essential"
              text="Login, security, saved lists and watch history, and remembering this choice. Always on."
              checked={true}
              disabled={true}
              onChange={() => {}}
            />
            <Row
              title="Analytics"
              text="Google Analytics, to understand which features are used. No ad personalisation."
              checked={analytics}
              onChange={setAnalytics}
            />
            {ADS_ENABLED && (
              <Row
                title="Advertising"
                text="Ads shown to free users, delivered by third-party ad partners who may set their own cookies."
                checked={advertising}
                onChange={setAdvertising}
              />
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button type="button" style={btn(false)} onClick={() => choose(false, false)}>
            Reject non-essential
          </button>
          <button type="button" style={btn(true)} onClick={() => choose(true, ADS_ENABLED)}>
            Accept all
          </button>
          {customizing ? (
            <button
              type="button"
              style={btn(false)}
              onClick={() => choose(analytics, advertising)}
            >
              Save my choices
            </button>
          ) : (
            <button
              type="button"
              style={btn(false)}
              onClick={() => setCustomizing(true)}
            >
              Customize
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
